import { createRouter, createWebHistory } from 'vue-router'
import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'
import ServersView from '@/views/ServersView.vue'
import ServersAddView from '@/views/ServersAddView.vue'
import ServersEditView from '@/views/ServersEditView.vue'
import OrganizationView from '@/views/OrganizationView.vue'
import OrganizationAdd from '@/views/OrganizationAdd.vue'
import OrganizationEdit from '@/views/OrganizationEdit.vue'
import ProfileView from '@/views/ProfileView.vue'
import { useAlertStore, useAuthStore, useOrganizationStore } from '@/store'
import Members from '@/components/organizationSettings/Members.vue'
import Billing from '@/components/organizationSettings/Billing.vue'
import Main from '@/components/organizationSettings/Main.vue'
import SSHKeys from '@/components/profileSettings/SSHKeys.vue'
import ProfileMain from '@/components/profileSettings/ProfileMain.vue'
import MemberEdit from '@/components/organizationSettings/MemberEdit.vue'
import ServerEdit from '@/components/server/ServerEdit.vue'
import BillingPlans from '@/components/organizationSettings/BillingPlans.vue'
import ServerUsers from '@/components/server/ServerUsers.vue'
import SSHKeyAdd from '@/components/profileSettings/keys/SSHKeyAdd.vue'
import SSHKeyList from '@/components/profileSettings/keys/SSHKeyList.vue'
import SSHKeyImport from '@/components/profileSettings/keys/SSHKeyImport.vue'
import FeedbackView from '@/views/FeedbackView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: 'active',
  routes: [
    {
      path: '/organizations',
      children: [
        { path: '', name: 'organizations:list', component: OrganizationView },
        { path: 'add', name: 'organizations:add', component: OrganizationAdd },
        {
          path: ':organization_id',
          component: OrganizationEdit,
          beforeEnter: (to, from, next) => {
            // check if user has permission to edit org
            next()
          },
          children: [
            {
              path: 'main',
              name: 'organizations:edit',
              component: Main
            },
            {
              path: 'members',
              children: [
                {
                  path: '',
                  name: 'organizations:members',
                  component: Members
                },
                {
                  path: ':member_id',
                  name: 'organizations:members:edit',
                  component: MemberEdit
                }
              ]
            },
            {
              path: 'billing',
              children: [
                {
                  path: '',
                  name: 'organizations:billing',
                  component: Billing
                },
                {
                  path: 'plans',
                  name: 'organizations:billing:plans',
                  component: BillingPlans
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/profile',
      component: ProfileView,
      children: [
        {
          path: 'main',
          name: 'account:profile',
          component: ProfileMain
        },
        {
          path: 'keys',
          component: SSHKeys,
          children: [
            { name: 'account:keys:list', path: '', component: SSHKeyList },
            { name: 'account:keys:add', path: 'add', component: SSHKeyAdd },
            {
              name: 'account:keys:github',
              path: 'github',
              component: SSHKeyImport,
              props: { provider: 'github' }
            },
            {
              name: 'account:keys:gitlab',
              path: 'gitlab',
              component: SSHKeyImport,
              props: { provider: 'gitlab' }
            }
          ]
        }
      ]
    },
    {
      path: '/account',
      children: [
        {
          path: 'login',
          name: 'account:login',
          component: LoginView,
          meta: {
            public: true
          }
        },
        {
          path: 'register',
          name: 'account:register',
          component: RegisterView,
          meta: {
            public: true
          }
        }
      ]
    },
    {
      path: '/servers',
      children: [
        {
          path: '',
          name: 'servers:list',
          component: ServersView,
          meta: {
            requiresPlan: true
          }
        },
        {
          path: 'add',
          name: 'servers:add',
          component: ServersAddView,
          meta: {
            requiresPlan: true
          }
        },
        {
          path: ':server_id',
          component: ServersEditView,
          children: [
            { path: 'edit', name: 'servers:edit', component: ServerEdit },
            { path: 'users', name: 'servers:users', component: ServerUsers }
          ],
          meta: {
            requiresPlan: true
          }
        }
      ]
    },
    {
      path: '/feedback',
      name: 'feedback',
      component: FeedbackView
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/organizations'
    }
  ]
})

router.beforeEach(async (to) => {
  // clear alert on route change
  const alertStore = useAlertStore()
  alertStore.clear()

  const authRequired = to.meta.public !== true
  const planRequired = to.meta.requiresPlan === true
  const authStore = useAuthStore()
  const organizationStore = useOrganizationStore()

  if (authRequired && !authStore.user) {
    authStore.returnUrl = to.fullPath
    return { name: 'account:login' }
  }
  if (authStore.user && to.name === 'account:login') {
    return { name: 'organizations:list' }
  }
  if (planRequired) {
    if (!organizationStore.hasOrganization) {
      return { name: 'organizations:list' }
    } else {
      const { data, error } = await organizationStore.currentOrganization()
      if (error.value) {
        organizationStore.clearOrganization()
      }
    }
  }
})

export default router
